import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { addOnAPI, trekAPI, adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import { PageHeader } from '../components/ui';
import './ModuleAdmin.css';

import { priceInputString } from '../utils/formatAdminPrice.js';

const emptyForm = { name: '', description: '', price: '', imageUrl: '', trekId: '', type: 'gear', order: '0' };

const AddOnForm = () => {
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  const { data: itemRes } = useQuery(
    ['add-on', id],
    () => addOnAPI.get(id),
    { enabled: !!isEdit && !!id }
  );
  const existing = itemRes?.data?.data;

  const { data: treksData } = useQuery(['treks'], () => trekAPI.list({ limit: 200 }));
  const treks = treksData?.data?.data || [];

  const [form, setForm] = useState(emptyForm);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name ?? '',
        description: existing.description ?? '',
        price: existing.price != null ? priceInputString(existing.price) : '',
        imageUrl: existing.imageUrl ?? '',
        trekId: existing.trekId ?? '',
        type: existing.type ?? 'gear',
        order: existing.order != null ? String(existing.order) : '0'
      });
    }
  }, [existing]);

  const mutation = useMutation(
    (payload) => (isEdit ? addOnAPI.update(id, payload) : addOnAPI.create(payload)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['add-ons']);
        navigate(-1);
        showToast({
          message: isEdit ? 'Add-on updated successfully.' : 'Add-on created successfully.',
          severity: 'success'
        });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Save failed.',
          severity: 'error'
        })
    }
  );

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url || res.data?.url;
      if (url) setForm((p) => ({ ...p, imageUrl: url }));
    } catch (err) {
      showToast({
        message: err?.response?.data?.message || 'Image upload failed.',
        severity: 'error'
      });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = () => {
    if (!form.name?.trim()) {
      showToast({
        message: 'Name is required.',
        severity: 'warning'
      });
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: (form.description || '').trim() || null,
      price: form.price ? Number(form.price) : 0,
      imageUrl: (form.imageUrl || '').trim() || null,
      trekId: form.trekId?.trim() || null,
      type: form.type || 'gear',
      order: parseInt(form.order, 10) || 0
    };
    mutation.mutate(payload);
  };

  return (
    <div className="module-page">
      <AdminBackLink to="/add-ons" label="Back to add-ons" />
      <PageHeader
        title={isEdit ? 'Edit Add-on' : 'Add Add-on'}
        subtitle={
          isEdit
            ? 'Update add-on details.'
            : 'Create an optional add-on for bookings (gear, activity, stay). Leave Trek blank to show for all treks.'
        }
      />

      <div className="module-card">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="module-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Rucksack Rental (60L)"
                required
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Description (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short description for the booking page"
                rows={2}
              />
            </div>
            <div>
              <label>Price (₹)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.price}
                onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                placeholder="0"
                required
              />
            </div>
            <div>
              <label>Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
              >
                <option value="gear">Gear rental</option>
                <option value="activity">Activity</option>
                <option value="stay">Stay upgrade</option>
              </select>
            </div>
            <div>
              <label>Trek (optional – blank = all treks)</label>
              <select
                value={form.trekId}
                onChange={(e) => setForm((p) => ({ ...p, trekId: e.target.value }))}
              >
                <option value="">All treks</option>
                {treks.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Order (display order)</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Image (optional)</label>
              <div className="attraction-form-image">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                  {isUploading ? 'Uploading...' : 'Choose image'}
                </button>
                {form.imageUrl && (
                  <div className="attraction-form-image-preview">
                    <img src={form.imageUrl} alt="Add-on" />
                    <button type="button" onClick={() => setForm((p) => ({ ...p, imageUrl: '' }))}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="legacy-main-actions">
            <button type="button" className="module-btn" disabled={mutation.isLoading} onClick={handleSubmit}>
              {mutation.isLoading ? 'Saving...' : 'Save'}
            </button>
        <button type="button" className="module-btn secondary" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOnForm;
